import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import * as Sentry from "@sentry/react"

/** Standard API response envelope matching backend structure */
export interface ApiResponse<T> {
    /** Response payload data */
    data: T;
    /** Response message for user feedback */
    message: string | null;
    /** Whether the request resulted in an error */
    isError: boolean;
}

/** Enhanced API response that includes HTTP metadata */
export interface EnhancedApiResponse<T> extends ApiResponse<T> {
    /** HTTP status code */
    status: number;
    /** HTTP status text */
    statusText: string;
}

/** Enhanced Axios instance with wrapped response types */
export interface EnhancedAxiosInstance {
    get<T = any>(url: string, config?: AxiosRequestConfig): Promise<EnhancedApiResponse<T>>;
    post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<EnhancedApiResponse<T>>;
    put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<EnhancedApiResponse<T>>;
    delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<EnhancedApiResponse<T>>;
    patch<T = any>(url: string, config?: AxiosRequestConfig): Promise<EnhancedApiResponse<T>>;
}

/** Base Axios instance */
const baseAxios: AxiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'https://staging-api.symphonyos.co:8080/api',
    // timeout: 60000,
    headers: {
        'Content-Type': 'application/json'
    }
});

/** Handle request errors */
const handleRequestError = (error: AxiosError): Promise<AxiosError> => {
    Sentry.captureException(error, {
        tags: {
            type: 'request_error'
        }
    });
    return Promise.reject(error);
};

/** Handle response errors */
const handleResponseError = (error: AxiosError): Promise<AxiosError> => {
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
        localStorage.removeItem('accessToken');
        window.location.href = '/auth/login';
        return Promise.reject(error);
    }

    // Log error to Sentry
    Sentry.captureException(error, {
        tags: {
            type: 'response_error',
            status: error.response?.status
        }
    });

    return Promise.reject(error);
};

// Request interceptor
baseAxios.interceptors.request.use((config) => {
    const token = localStorage.getItem('accessToken');

    if (config.headers) {
        config.headers.Authorization = token ? `Bearer ${token}` : '';
    }

    return config;
}, handleRequestError);

// Response interceptor
baseAxios.interceptors.response.use(
    (response: AxiosResponse) => response,
    handleResponseError
);

/** Create wrapped version of Axios with proper types */
const Axios: EnhancedAxiosInstance = {
    get: async<T = any>(url: string, config?: AxiosRequestConfig) => {
        const response = await baseAxios.get<ApiResponse<T>>(url, config);
        return {
            data: response.data.data, // Unwrap the nested data
            message: response.data.message,
            isError: response.data.isError,
            status: response.status,
            statusText: response.statusText
        };
    },
    post: async<T = any>(url: string, data?: any, config?: AxiosRequestConfig) => {
        const response = await baseAxios.post<ApiResponse<T>>(url, data, config);
        return {
            data: response.data.data, // Unwrap the nested data
            message: response.data.message,
            isError: response.data.isError,
            status: response.status,
            statusText: response.statusText
        };
    },
    put: async<T = any>(url: string, data?: any, config?: AxiosRequestConfig) => {
        const response = await baseAxios.put<ApiResponse<T>>(url, data, config);
        return {
            data: response.data.data, // Unwrap the nested data
            message: response.data.message,
            isError: response.data.isError,
            status: response.status,
            statusText: response.statusText
        };
    },
    patch: async<T = any>(url: string, config?: AxiosRequestConfig) => {
        const response = await baseAxios.delete<ApiResponse<T>>(url, config);
        return {
            data: response.data.data, // Unwrap the nested data
            message: response.data.message,
            isError: response.data.isError,
            status: response.status,
            statusText: response.statusText
        };
    },
    delete: async<T = any>(url: string, config?: AxiosRequestConfig) => {
        const response = await baseAxios.delete<ApiResponse<T>>(url, config);
        return {
            data: response.data.data, // Unwrap the nested data
            message: response.data.message,
            isError: response.data.isError,
            status: response.status,
            statusText: response.statusText
        };
    },
};

/** Updates Axios default headers */
export const updateAxiosDefaults = {
    /** Sets the brand ID header for all subsequent requests */
    setBrandId: (brandId: string | number) => {
        baseAxios.defaults.headers.common['brand-id'] = brandId;
    },
    /** Clears the brand ID header */
    clearBrandId: () => {
        delete baseAxios.defaults.headers.common['brand-id'];
    }
};

export default Axios;